import { ReactNode, ChangeEvent, useState, useCallback, memo } from 'react'

import { AccordionProps, createStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordion from '@material-ui/core/Accordion'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import CloseIcon from '@material-ui/icons/Close'
import { TransitionProps } from '@material-ui/core/transitions'
import { withStyles } from '@material-ui/styles'
import Grow from '@material-ui/core/Grow'

import Markdown from 'components/Markdown'
import { isTenantSmava } from 'utils/url'

interface OwnProps {
  title: ReactNode
  onChange?: (isExpanded: boolean) => void
  isClosable?: boolean
  onClose?: () => void
}

type IntroductionProps = Omit<AccordionProps, 'onChange' | 'title'> & OwnProps

const defaultTransitionProps: TransitionProps = { unmountOnExit: true }

const Accordion = withStyles((theme: Theme) => ({
  root: {
    '&::before': {
      opacity: 0
    },
    backgroundColor: isTenantSmava()
      ? theme.palette.grey[50]
      : theme.palette.primary.light,
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    marginTop: `${theme.spacing(4)}px !important`,
    marginBottom: `${theme.spacing(4)}px !important`,
    transition: theme.transitions.create('box-shadow', { duration: 300 })
  },
  expanded: {
    boxShadow: theme.shadows[4]
  }
}))(MuiAccordion)

const Summary = withStyles((theme) =>
  createStyles({
    expandIcon: {
      marginRight: ({ isClosable }: { isClosable?: boolean }) =>
        isClosable ? theme.spacing(2) : undefined
    }
  })
)(AccordionSummary)

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative'
  },
  close: {
    position: 'absolute',
    top: theme.spacing(1) * 0.5,
    right: theme.spacing(1) * 0.5,
    cursor: 'pointer'
  },
  heading: {
    fontSize: '1.125rem',
    lineHeight: '1.5rem'
  },
  body: {
    '&>div': {
      '&> *:first-child': {
        marginTop: 0
      },
      '&>h3': {
        fontSize: '1.0625rem',
        lineHeight: isTenantSmava() ? '1.75rem' : 'inherit'
      }
    }
  }
}))

const Introduction = ({
  title,
  children,
  expanded = false,
  onChange,
  isClosable = false,
  onClose,
  ...restProps
}: IntroductionProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded)
  const [isClosed, setIsClosed] = useState(false)

  const { container, heading, body, close } = useStyles()

  const handleChange = useCallback(
    (_: ChangeEvent<unknown>, didExpand: boolean) => {
      setIsExpanded(didExpand)

      if (onChange) {
        onChange(didExpand)
      }
    },
    [setIsExpanded, onChange]
  )

  const handleClose = useCallback(() => {
    if (!isClosable) {
      return
    }

    if (onClose) {
      onClose()
    }

    setIsClosed(true)
  }, [isClosable, onClose])

  if (isClosed) {
    return null
  }

  return (
    <Grow in>
      <div className={container}>
        <Accordion
          TransitionProps={defaultTransitionProps}
          expanded={isExpanded}
          onChange={handleChange}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...restProps}
        >
          <Summary
            expandIcon={<ArrowDropDownIcon color="primary" />}
            aria-controls="Introduction-content"
          >
            {typeof title === 'string' ? (
              <Typography
                variant={isTenantSmava() ? 'h5' : 'inherit'}
                className={heading}
              >
                {title}
              </Typography>
            ) : (
              title
            )}
          </Summary>
          <AccordionDetails>
            <Typography
              variant={isTenantSmava() ? 'caption' : 'inherit'}
              component="div"
              className={body}
            >
              <Markdown>{children}</Markdown>
            </Typography>
          </AccordionDetails>
        </Accordion>
        {isClosable && (
          <CloseIcon
            color="disabled"
            fontSize="small"
            aria-label="close"
            onClick={handleClose}
            className={close}
          />
        )}
      </div>
    </Grow>
  )
}

export default memo(Introduction)
